import {
  Box,
  H4,
  Spinner,
  ListSensorInfo,
} from '@ouster/webviz-react-components';

import { Chip } from '@mui/material';
import { LuScan } from 'react-icons/lu';

import DashboardCard from './DashboardCard';
import InfoItem from './InfoItem';
import { InfoItemType } from './types';
import { useStoreState } from '../../reducers';

interface SensorBoxProps {
  sensor: ListSensorInfo;
}

function SensorBox({ sensor }: SensorBoxProps) {
  const sensorInfoItems: InfoItemType[] = [
    { label: 'Resolution', value: sensor.sdk_prod_resolution ?? 'N/A' },
    { label: 'Lidar Mode', value: sensor.sdk_lidar_mode ?? 'N/A' },
    { label: 'Beam Spacing', value: sensor.sdk_prod_beam_spacing ?? 'N/A' },
    { label: 'Firmware Version', value: sensor.sdk_fw_version_core ?? 'N/A' },
    { label: 'Hardware Rev', value: sensor.sdk_prod_hw_rev ?? 'N/A' },
    { label: 'Part Number', value: sensor.sdk_prod_pn ?? 'N/A' },
  ];

  return (
    <Box
      border="1px solid"
      borderColor="whiteAlpha.300"
      borderRadius="md"
      p={4}
      mb={4}
      bgColor="whiteAlpha.25"
      data-testid={`sensor-box-${sensor.sdk_prod_sn}`}
    >
      <Box
        mb={8}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box>
          <H4 fontSize={14}>{sensor.sdk_prod_line}</H4>
          <Box as="p" color="gray.300">
            Serial: {sensor.sdk_prod_sn}
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Chip label={sensor.sdk_prod_type} size="small" />
          <Chip label={sensor.sdk_prod_generation} size="small" />
        </Box>
      </Box>
      <Box
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        columnGap={4}
        rowGap={4}
        data-testid={`sensor-info-grid-${sensor.sdk_prod_sn}`}
      >
        {sensorInfoItems.map((item) => (
          <InfoItem
            key={item.label}
            label={item.label}
            value={item.value}
            color={item.color}
            data-testid={`sensor-info-item-${sensor.sdk_prod_sn}-${item.label
              .toLowerCase()
              .replace(/\s/g, '-')}`}
          />
        ))}
      </Box>
    </Box>
  );
}

export default function RecordingLookUpSensors() {
  const { recordingLookupResult, isLoadingRecordingLookup } = useStoreState(
    (storeState) => storeState.adminDashboard
  );

  const cardProps = {
    icon: <LuScan size={20} />,
    title: `Sensor Data`,
  };

  if (isLoadingRecordingLookup) {
    return (
      <DashboardCard {...cardProps}>
        <Box display="flex" alignItems="center" justifyContent="center" my={8}>
          <Spinner />
        </Box>
      </DashboardCard>
    );
  }

  if (!recordingLookupResult) return null;

  const { sensor_info: sensors } = recordingLookupResult;
  cardProps.title = `Sensor Data (${sensors.length})`;

  return (
    <DashboardCard {...cardProps} data-testid="recording-lookup-sensors-card">
      <Box display="grid" rowGap={2}>
        {sensors.map((sensor) => (
          <SensorBox key={sensor.sdk_prod_sn} sensor={sensor} />
        ))}
      </Box>
    </DashboardCard>
  );
}
